import React, { useEffect, useRef, useState } from "react";

import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import LoadingModal from "@/common/LoadingModal";
import { importBulletinFromReport } from "@/components/alerts/add-v2/persistence/reportImport";
import { bulletinEditPath } from "@/components/alerts/add-v2/routePaths";

/**
 * The landing spot for `?token=` — a bulletin arriving from a RubiREPORT occurrence.
 *
 * There is nothing to choose here, so there is nothing on screen but the wait: the report page opens
 * this in a new tab, the row is created server-side (decision 53), and the officer is sent to the
 * edit route as soon as it has an id.
 *
 * ## The attempt runs once, deliberately
 *
 * The call **creates a bulletin**, and React's development double-mount would otherwise make two of
 * them — or rather one bulletin and one 404, since the first attempt spends the token, which is worse
 * because the error is the half the officer would see. A ref rather than a state flag: it has to be
 * true before the second effect runs, not after the next render.
 *
 * A failure is offered a retry because it *is* retryable: the hand-off is consumed only after the
 * bulletin is committed, so a call that failed left the token spendable.
 */
export const ReportHandoff: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation("bulletin/create-v2");
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  /**
   * Which import is the current one, as `token:attempt` — not a boolean, and not a per-run flag.
   *
   * It used to be a boolean guard plus a `current` closure variable cleared on cleanup, which
   * under React 18 StrictMode disowned the only import there was: run 1 started it, run 1's
   * cleanup set its `current` to `false`, and run 2 returned early because the boolean was
   * already `true`. The bulletin got created and the officer sat on the spinner.
   *
   * A ref survives the effect re-running, so the resolved import can still recognise itself.
   * `attempt` is in the key, which is what re-arms this for the retry button.
   */
  const running = useRef<string | null>(null);

  useEffect(() => {
    const key = `${token}:${attempt}`;
    if (running.current === key) return;
    running.current = key;

    importBulletinFromReport(token)
      .then((bulletinId) => {
        if (running.current !== key) return;
        // Replaced, not pushed: the token is spent, so Back must not return to a URL that now
        // answers 404. The draft itself is where the officer already is.
        navigate(bulletinEditPath(bulletinId), { replace: true });
      })
      .catch((error) => {
        console.error("Failed to create a bulletin from a report:", error);
        if (running.current !== key) return;
        setFailed(true);
      });
  }, [token, navigate, attempt]);

  return (
    <Box
      sx={{
        maxWidth: "var(--page-max-width)",
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      {failed ? (
        <>
          <Typography variant="body1">{t("start.report_failed")}</Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setFailed(false);
              setAttempt((previous) => previous + 1);
            }}
          >
            {t("start.report_retry")}
          </Button>
        </>
      ) : (
        <LoadingModal
          stages={[
            t("start.report_stage_reading"),
            t("start.report_stage_entities"),
            t("start.report_stage_files"),
          ]}
          timingMessage={t("start.extract_timing")}
        />
      )}
    </Box>
  );
};
